'use client';

import React, { useState, useEffect } from 'react';
import Sidebar from '@/components/layout/Sidebar';
import MobileNav from '@/components/layout/MobileNav';
import CategorySection from '@/components/cards/CategorySection';
import Search from '@/components/ui/Search';
import LanguageToggle from '@/components/ui/LanguageToggle';
import { Resource, Category, categories as defaultCategories } from '@/data/resources';
import ResourceCard from '@/components/cards/ResourceCard';
import { useTranslation } from '@/hooks/useTranslation';

export default function Home() {
  const [searchResults, setSearchResults] = useState<Resource[]>([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [categories, setCategories] = useState(defaultCategories);
  const isSearching = searchResults.length > 0;
  const { t, language } = useTranslation();
  
  // 避免水合错误
  useEffect(() => {
    setMounted(true);
  }, []);
  
  // 加载 JSON 配置的网站
  useEffect(() => {
    // 首次加载网站配置
    const loadWebsites = async () => {
      try {
        // 添加时间戳避免缓存问题
        const timestamp = Date.now();
        // 使用绝对URL以确保在所有环境中都能正确加载
        const baseUrl = window.location.origin;
        const apiUrl = `${baseUrl}/api/websites?lang=${language}&t=${timestamp}`;
        console.log('Fetching from API:', apiUrl);
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        if (!response.ok) {
          console.warn(`API responded with status: ${response.status}`);
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log("API response data:", data);
        
        if (data && data.categories) {
          // 确保在更新状态前进行数据验证
          const validCategories = ensureCategoriesValid(data.categories);
          setCategories(validCategories);
          console.log('Successfully loaded website data from API');
        } else {
          throw new Error('Invalid or empty data from API');
        }
      } catch (error) {
        console.error("Error loading website config:", error);
        // 如果API调用失败，使用默认分类
        const validCategories = ensureCategoriesValid(defaultCategories);
        setCategories(validCategories);
        
        // 5秒后重试一次
        setTimeout(() => {
          console.log('Retrying website data load...');
          loadWebsites();
        }, 5000);
      }
    };
    
    // 确保分类数据有效，特别是确保GitHub卡片存在
    const ensureCategoriesValid = (categories: Category[]): Category[] => {
      if (!Array.isArray(categories)) {
        console.warn('Categories is not an array, using defaults');
        return addGithubCard(defaultCategories);
      }
      
      return addGithubCard(categories);
    };
    
    // 特别添加GitHub卡片到部署类别
    const addGithubCard = (categories: Category[]): Category[] => {
      // 创建一个深拷贝以避免修改原始数据
      const categoriesCopy = JSON.parse(JSON.stringify(categories));
      
      // 查找deployment类别
      const deploymentCategory = categoriesCopy.find((cat: Category) => cat.id === 'deployment');
      if (deploymentCategory) {
        // 检查GitHub资源是否存在
        const hasGithub = deploymentCategory.resources.some((res: Resource) => res.id === 'github');
        if (!hasGithub) {
          // 如果不存在，手动添加GitHub资源
          console.log('Adding GitHub card to deployment category');
          deploymentCategory.resources.push({
            id: 'github',
            name: 'GitHub',
            description: language === 'zh' ? 
              '代码版本控制和协作平台' : 
              'Platform for version control and collaboration',
            url: 'https://github.com',
            icon: ''
          });
        }
      } else {
        // 如果没有找到deployment类别，创建一个
        console.log('Creating deployment category with GitHub card');
        categoriesCopy.push({
          id: 'deployment',
          name: language === 'zh' ? '部署' : 'Deployment',
          icon: 'ServerIcon',
          resources: [{
            id: 'github',
            name: 'GitHub',
            description: language === 'zh' ? 
              '代码版本控制和协作平台' : 
              'Platform for version control and collaboration',
            url: 'https://github.com',
            icon: ''
          }]
        });
      }
      
      return categoriesCopy;
    };
    
    // 立即加载一次
    loadWebsites();
    
    // 设置轮询检查配置文件是否变更
    const interval = setInterval(loadWebsites, 60000);
    
    return () => clearInterval(interval);
  }, [language]);
  
  // 如果组件尚未挂载，显示一个简单的加载状态
  if (!mounted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <span className="text-gray-600 dark:text-gray-300">Loading...</span>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      {/* Desktop Sidebar - hidden on mobile */}
      <div className="hidden lg:block">
        <Sidebar categories={categories} />
      </div>
      
      <main className="flex-1 lg:ml-64 p-4 sm:p-6 lg:p-8 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center space-x-4">
              <MobileNav isOpen={mobileMenuOpen} setIsOpen={setMobileMenuOpen} categories={categories} />
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {t('siteTitle')}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <LanguageToggle />
            </div>
          </div>
          
          <Search onSearch={setSearchResults} categories={categories} />
          
          {isSearching ? (
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                {t('searchResults')}
              </h2>
              {searchResults.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {searchResults.map((resource) => (
                    <ResourceCard key={resource.id} resource={resource} />
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">
                  {t('noResultsFound')}
                </p>
              )}
            </div>
          ) : (
            <>
              {categories.map((category) => (
                category.resources.length > 0 && (
                  <CategorySection key={category.id} category={category} />
                )
              ))}
            </>
          )}
          
          {/* Footer with friendly links and copyright information */}
          <footer className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-800">
            {/* Friendly Links Section */}
            <div className="text-center mb-5">
              <div className="inline-flex items-center justify-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {t('friendlyLinks')}:
                </span>
                <a 
                  href="https://link.zhihu.com/?target=https://asphaltcalculator.tools/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:underline transition-all"
                >
                  {t('asphaltCalculator')}
                </a>
              </div>
            </div>
            
            <div className="text-center text-gray-600 dark:text-gray-400 text-sm">
              <p>{t('copyright')}</p>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
}
